import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const mentonEn: ChauffeurPage = {
  slug: "menton",
  title: "VTC & private chauffeur in Menton",
  subtitle:
    "Private chauffeur in Menton: the town on the Italian border, the Lemon Festival, the gardens and old town, with a ~45 min transfer to Nice airport.",
  keywords: [
    "vtc menton",
    "private chauffeur menton",
    "menton ventimiglia chauffeur",
    "menton nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Menton",
    subtitle:
      "The pearl of France at Italy’s door: gardens, the Lemon Festival and crossings to Ventimiglia, with a dedicated chauffeur.",
    keywords: [
      "VTC Menton",
      "Ventimiglia chauffeur",
      "Nice airport transfer",
      "Lemon Festival chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Menton",
    badges: ["Italian border", "Lemon Festival", "~45 min from the airport"],
  },
  benefits: {
    heading: "Why a chauffeur in Menton",
    items: [
      {
        title: "On the Italian border",
        description:
          "The last town before Liguria: Ventimiglia is a few minutes away, notably for its Friday market, with no parking to worry about on the other side.",
      },
      {
        title: "Lemon Festival & gardens",
        description:
          "The Biovès Garden during the Lemon Festival, Serre de la Madone, Val Rahmeh: drop-off as close as possible to the entrances, on the dates and times that suit you.",
      },
      {
        title: "Old town & Cocteau",
        description:
          "Sloping lanes up to the Saint-Michel-Archange basilica and the Jean Cocteau museum: the chauffeur leaves you at the foot of the pedestrian old centre.",
      },
      {
        title: "~45 minutes from the airport",
        description:
          "The longest of our towns’ runs to Nice airport: a direct transfer with flight tracking and a comfortable time margin.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Menton",
    services: [
      {
        title: "Nice airport transfers",
        text: "Menton ⇄ Nice airport in about 45 minutes, with flight tracking and an earlier departure time for this longer run.",
      },
      {
        title: "Crossings into Italy",
        text: "Trips to Ventimiglia, Bordighera and San Remo, notably for the Friday market, with waiting and return arranged.",
      },
      {
        title: "Lemon Festival",
        text: "Shuttles and drop-off as close as possible to the Biovès Garden during the Lemon Festival, despite the crowds and closed streets.",
      },
      {
        title: "Gardens of Menton",
        text: "Serre de la Madone, Val Rahmeh, Jardin Maria Serena and Fontana Rosa: a gardens circuit with visiting time included.",
      },
      {
        title: "Hourly hire & surroundings",
        text: "By the hour or by the day to link the old town, Roquebrune-Cap-Martin and Monaco, close by.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old town",
        description:
          "Lanes and ramps up to the Saint-Michel-Archange basilica.",
      },
      {
        name: "Biovès Garden",
        description:
          "The setting of the Lemon Festival, between the sea and the old town.",
      },
      {
        name: "Serre de la Madone",
        description: "A terraced botanical garden in the heights of Menton.",
      },
      {
        name: "Val Rahmeh",
        description: "An exotic garden and its collections, near Garavan.",
      },
      {
        name: "Jean Cocteau Museum",
        description: "The Bastion and the collection, on the seafront.",
      },
      {
        name: "Ventimiglia border",
        description:
          "The crossing into Italy via Menton-Garavan and Ponte San Luigi.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur for the border town of gardens",
    subheading:
      "Menton blends a mild microclimate, botanical gardens and immediate closeness to Italy.",
    paragraphs: [
      "Nestled between sea and mountain, Menton is the last French town before Liguria. Its microclimate feeds exceptional gardens — Serre de la Madone, Val Rahmeh — and its Lemon Festival colours the Biovès Garden in winter; a chauffeur takes you from one site to the next while handling the crowds and the parking.",
      "KS Rent Services offers fixed fares set in advance — including Menton ⇄ Nice airport — and hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, are rated 5.0/5 from 38 Google reviews.",
      "The Italian border is within reach: Ventimiglia and its market, Bordighera, San Remo. The chauffeur arranges the round trip, waits while you shop and brings you back to Menton or your hotel, with no need for you to drive across the border.",
    ],
    bullets: [
      "Ventimiglia crossings with waiting and return arranged",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Menton",
    steps: [
      "Share your programme: airport transfer, gardens, Lemon Festival or a crossing into Italy.",
      "We confirm the vehicle and the fixed fare or the hourly hire.",
      "The chauffeur picks you up at your hotel, villa or in the old town.",
      "Live adjustments to the crowds, the timings and the border crossing.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Menton"),
    {
      q: "How long does it take to reach Nice airport from Menton?",
      a: "About 45 minutes, the longest of our runs, via the A8. The chauffeur tracks your flight and allows a time margin suited to that distance.",
    },
    {
      q: "Can you take us into Italy, to Ventimiglia?",
      a: "Yes. Ventimiglia is a few minutes from Menton: we arrange the round trip, notably for the Friday market, with waiting while you shop and a return to Menton.",
    },
    {
      q: "Do you operate during the Lemon Festival?",
      a: "Yes. During the Lemon Festival we run shuttles and drop off as close as possible to the Biovès Garden, despite the crowds and the streets closed to traffic.",
    },
    {
      q: "Do you offer a tour of Menton’s gardens?",
      a: "Yes, on hourly hire to string together Serre de la Madone, Val Rahmeh, Jardin Maria Serena and Fontana Rosa, with visiting time included.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book early during the Lemon Festival and in summer; the rest of the year, last-minute requests are handled subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, comfortable with an international clientele and frequent crossings into Italy.",
    },
    {
      q: "Which vehicles do you offer for Menton?",
      a: "A Mercedes sedan, a 7-seat van or an SUV depending on the number of passengers, suited to long-run transfers as well as cross-border trips.",
    },
  ],
  relatedLinks: [
    { label: "Meet and greet at Nice Airport", href: "/en/chauffeur/airport" },
    { label: "Private chauffeur in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    {
      label: "French Riviera rates and routes",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Menton",
    text: "Airport transfer, gardens, the Lemon Festival or a crossing into Italy: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Menton 24/7 — car service & transfers",
    description:
      "Private car service in Menton: Italian border and Ventimiglia, Lemon Festival, gardens and a ~45 min transfer to Nice airport. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/menton",
  },
};

export default mentonEn;
